<template>
  <div>
    <HeroHeader
      eyebrow="RECAUDACIÓN"
      :title="months[selectedMonth] + ' ' + selectedYear"
      :counter-num="collectionRate + '%'"
      counter-unit="recaudado"
    >
      <template #toolbar>
        <select v-model="selectedMonth" class="select select--compact" @change="loadData">
          <option v-for="(m, i) in months" :key="i" :value="i">{{ m }}</option>
        </select>
        <select v-model="selectedYear" class="select select--compact" @change="loadData">
          <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
        </select>
        <button class="btn" @click="$router.push({name: 'pending-bills'})">Ver pendientes</button>
      </template>
    </HeroHeader>

    <div class="stat-strip">
      <div class="stat-strip__item">
        <span class="stat-strip__label">Esperado</span>
        <span class="stat-strip__value">{{ formatUSD(totalExpected) }}</span>
      </div>
      <div class="stat-strip__item">
        <span class="stat-strip__label">Recaudado</span>
        <span class="stat-strip__value stat-strip__value--success">{{ formatUSD(totalCollected) }}</span>
      </div>
      <div class="stat-strip__item">
        <span class="stat-strip__label">Pendiente</span>
        <span class="stat-strip__value" :class="totalPending > 0 ? 'stat-strip__value--error' : ''">{{ formatUSD(totalPending) }}</span>
      </div>
    </div>

    <div class="filter-bar filter-bar--no-top" v-if="estateData.length > 0">
      <SortSelect :options="sortOptions" v-model="sortBy" />
    </div>

    <div v-if="estateData.length > 0" class="collection-list">
      <div class="collection-card" v-for="estate in sortedEstateData" :key="estate.id">

        <!-- Fila de propiedad -->
        <div
          class="prop-row prop-row--clickable"
          :class="{ 'prop-row--active': isExpanded(estate.id) }"
          @click="toggleEstate(estate.id)"
        >
          <div class="prop-row__head">
            <div class="prop-row__info">
              <div class="prop-row__name-row">
                <div class="prop-row__name">{{ estate.description }}</div>
                <button
                  type="button"
                  class="prop-row__open"
                  aria-label="Ver detalle de la propiedad"
                  title="Ver detalle"
                  @click.stop="$router.push({ name: 'estate-detail', params: { id: estate.id } })"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                       fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </button>
              </div>
              <div class="prop-row__address">
                {{ estate.tenants.length }} expositor{{ estate.tenants.length !== 1 ? 'es' : '' }}
                <span v-if="estate.overdueCount > 0" class="prop-row__address--overdue">
                  · {{ estate.overdueCount }} atrasado{{ estate.overdueCount !== 1 ? 's' : '' }}
                </span>
              </div>
            </div>
            <div class="prop-row__right" style="display:flex;align-items:center;gap:12px">
              <span class="prop-row__pct" :class="pctClass(estate.percentage)">{{ estate.percentage }}<sup>%</sup></span>
              <span class="prop-row__chevron" :class="{ 'prop-row__chevron--open': isExpanded(estate.id) }">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </span>
            </div>
          </div>
          <div class="prop-row__bar">
            <div class="prop-row__bar-fill" :class="barFillClass(estate.percentage)"
                 :style="{ width: Math.min(estate.percentage, 100) + '%' }"/>
          </div>
          <div class="prop-row__foot">
            <span class="prop-row__collected">{{ formatUSD(estate.collected) }}</span>
            <span class="prop-row__total">de {{ formatUSD(estate.expected) }}</span>
          </div>
        </div>

        <!-- Sub-filas de inquilinos (expandibles) -->
        <transition name="expand">
        <div v-if="isExpanded(estate.id)" class="collection-sub">
          <template v-if="estate.tenants.length > 0">
            <div
              v-for="t in estate.tenants"
              :key="t.id"
              class="tenant-row"
              :class="tenantRowClass(t)"
            >
              <div class="tenant-row__body">
                <div class="tenant-row__name">{{ t.name }}</div>
                <div class="tenant-row__meta">
                  <template v-if="t.rentUSD">{{ formatUSD(t.rentUSD) }}/mes</template>
                  <template v-else>Sin contrato vigente</template>
                  <span v-if="t.noContract" class="tenant-row__flag">sin contrato</span>
                </div>
              </div>
              <div class="tenant-row__right">
                <span class="tenant-row__status" :class="tenantStatusClass(t)">
                  {{ tenantStatusLabel(t) }}
                </span>
                <span v-if="!t.paid && t.overdue && t.overdueSince" class="tenant-row__detail">
                  {{ overduePeriod(t.overdueSince) }}
                </span>
              </div>
              <div class="tenant-row__actions">
                <button v-if="!t.paid && t.bill" class="btn btn--sm"
                        @click.stop="openPay(t.bill)">
                  Registrar pago
                </button>
              </div>
            </div>
          </template>
          <div v-else class="collection-sub__empty">Sin expositores activos este mes</div>
        </div>
        </transition>

      </div>
    </div>

    <EmptyState v-else title="Sin datos" message="No hay propiedades o contratos para este período."/>

    <PayBill
      :data="sidebarData"
      :show-navbar="showPaySidebar"
      :class="{'show': showPaySidebar}"
      @close="showPaySidebar = false"
      @update="onPayUpdate"
      @added="onBillAdded"
    />
  </div>
</template>

<script>
import { Timestamp, db } from '@/firebase/firebaseConfig'
import PayBill from '../bills/Pay'
import HeroHeader from '@/components/HeroHeader'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import { sortItems } from '@/utils/sort'
import axios from 'axios'

export default {
  name: 'CollectionDashboard',
  components: { PayBill, HeroHeader, EmptyState, SortSelect },
  data () {
    return {
      estates: [],
      clients: [],
      contracts: [],
      bills: [],
      exchangeRate: 3.7,
      selectedMonth: new Date().getMonth(),
      selectedYear: new Date().getFullYear(),
      months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
      expandedIds: [],
      sortBy: 'expected_desc',
      sortOptions: [
        { value: 'collected_desc', label: 'Mayor recaudación', field: 'collected', type: 'number', dir: 'desc' },
        { value: 'collected_asc', label: 'Menor recaudación', field: 'collected', type: 'number', dir: 'asc' },
        { value: 'pct_desc', label: 'Mayor % de avance', field: 'percentage', type: 'number', dir: 'desc' },
        { value: 'pct_asc', label: 'Menor % de avance', field: 'percentage', type: 'number', dir: 'asc' },
        { value: 'expected_desc', label: 'Mayor monto esperado', field: 'expected', type: 'number', dir: 'desc' },
        { value: 'overdue_desc', label: 'Más atrasados', field: 'overdueCount', type: 'number', dir: 'desc' },
        { value: 'name', label: 'Nombre (A–Z)', field: 'description', type: 'string', dir: 'asc' },
        { value: 'name_desc', label: 'Nombre (Z–A)', field: 'description', type: 'string', dir: 'desc' }
      ],
      showPaySidebar: false,
      sidebarData: { client: { currency: { code: '' } } }
    }
  },
  computed: {
    years () {
      const y = new Date().getFullYear()
      return [y - 1, y, y + 1]
    },
    // Recaudado por local = devengado por VENCIMIENTO: suma de las letras del mes
    // (this.bills, filtradas por deadline) que están pagadas, agrupadas por local →
    // { [estateId]: USD }. Una letra de junio pagada en julio cuenta en JUNIO.
    collectedByEstate () {
      const map = {}
      Object.keys(this.billsByClient).forEach(cid => {
        const localId = this.estateOfClient[cid]
        this.billsByClient[cid].forEach(b => {
          if (!b.paid) return
          map[localId] = (map[localId] || 0) + this.billUSD(b)
        })
      })
      return map
    },
    // Letras del mes agrupadas por inquilino. Un inquilino puede tener varias en un mismo mes
    // (cargo extra, segundo puesto, letra partida); quedarse con la primera daba un estado de
    // pago falso y un botón de cobro apuntando a la letra equivocada.
    // Local de cada inquilino ACTIVO (la ficha manda, no el snapshot viejo de la letra).
    estateOfClient () {
      const map = {}
      this.clients.forEach(c => { if (c.local && c.local.id) map[c.id] = c.local.id })
      return map
    },
    // Letras del mes por inquilino, sólo de fichas activas.
    billsByClient () {
      const map = {}
      this.bills.forEach(b => {
        const cid = b.client && b.client.id
        if (!cid || !this.estateOfClient[cid]) return
        if (!map[cid]) map[cid] = []
        map[cid].push(b)
      })
      return map
    },
    // Pendiente por local = Σ de las letras del mes SIN pagar → { [estateId]: USD }.
    estateData () {
      const monthEnd = new Date(this.selectedYear, this.selectedMonth + 1, 0, 23, 59, 59)
      const now = new Date()
      return this.estates.map(estate => {
        const tenants = this.clients
          .filter(c => c.local && c.local.id === estate.id)
          .map(client => {
            const contract = this.getActiveContract(client.id)
            const bills = this.billsByClient[client.id] || []
            // Sin contrato vigente y sin letras del mes no hay nada que mostrar ni que cobrar.
            if (!contract && !bills.length) return null
            const paidAmount = bills.filter(b => b.paid).reduce((s, b) => s + this.billUSD(b), 0)
            const rentUSD = contract ? this.toUSD(contract.rent, contract.currency ? contract.currency.code : 'PEN') : 0
            // Mismo criterio que la cifra del local: lo que le falta para su renta del mes.
            const pendingAmount = rentUSD - paidAmount
            const overdueSince = client.overdueSince
              ? (client.overdueSince.toDate ? client.overdueSince.toDate() : new Date(client.overdueSince))
              : null
            return {
              id: client.id,
              name: client.alias || client.name,
              rent: contract ? contract.rent : 0,
              rentUSD,
              pendingUSD: pendingAmount,
              paid: pendingAmount <= 0,
              overdue: pendingAmount > 0 && monthEnd < now,
              noBills: bills.length === 0,
              noContract: !contract,
              overdueSince,
              // El botón de cobro apunta a la primera letra SIN pagar, no a la primera del mes.
              bill: bills.find(b => !b.paid) || null
            }
          })
          .filter(Boolean)

        const expected = tenants.reduce((s, t) => s + t.rentUSD, 0)
        const collected = this.collectedByEstate[estate.id] || 0
        // Pendiente = Esperado − Recaudado, sobre los mismos inquilinos (activos). Puede salir
        // negativo cuando se cobró más que la renta del mes (adelantos, letras de otro mes).
        const pending = expected - collected
        const percentage = expected > 0 ? Math.round((collected / expected) * 100) : 0
        const overdueCount = tenants.filter(t => t.overdue).length
        return { id: estate.id, description: estate.description, expected, collected, pending, percentage, overdueCount, tenants }
      })
    },
    sortedEstateData () {
      return sortItems(this.estateData, this.sortOptions.find(o => o.value === this.sortBy))
    },
    totalExpected () { return this.estateData.reduce((s, e) => s + e.expected, 0) },
    totalCollected () { return this.estateData.reduce((s, e) => s + e.collected, 0) },
    totalPending () { return this.estateData.reduce((s, e) => s + e.pending, 0) },
    collectionRate () {
      return this.totalExpected > 0 ? Math.round((this.totalCollected / this.totalExpected) * 100) : 0
    }
  },
  async created () { await this.loadData() },
  methods: {
    async loadData () {
      try {
        this.$store.dispatch('showProgress')
        await Promise.all([this.getExchangeRate(), this.getEstates(), this.getClients(), this.getContracts(), this.getBillsForMonth()])
      } catch (e) {
        this.$toast.error('Error al cargar datos')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    async getEstates () {
      this.estates = []
      const snap = await db.collection('estates').get()
      snap.forEach(doc => this.estates.push({ id: doc.id, ...doc.data() }))
    },
    // Monto de una letra en USD, con la moneda de la propia letra.
    billUSD (b) {
      return this.toUSD(b.total, b.client && b.client.currency ? b.client.currency.code : 'PEN')
    },
    async getClients () {
      // REGLA DEL NEGOCIO: sólo cuentan los inquilinos ACTIVOS, y vale para las TRES cifras
      // —esperado, recaudado y pendiente—. Dar de baja una ficha es cómo se saca a un inquilino
      // del cálculo, así que las letras de una ficha de baja no suman en ninguna de las tres.
      this.clients = []
      const snap = await db.collection('clients').where('state', '==', true).get()
      snap.forEach(doc => this.clients.push({ id: doc.id, ...doc.data() }))
    },
    async getContracts () {
      this.contracts = []
      const snap = await db.collectionGroup('contracts').get()
      snap.forEach(doc => {
        const d = doc.data()
        this.contracts.push({
          id: doc.id,
          clientId: doc.ref.parent.parent.id,
          rent: d.rent,
          currency: d.currency,
          start: d.start ? d.start.toDate() : null,
          end: d.end ? d.end.toDate() : null
        })
      })
    },
    getActiveContract (clientId) {
      const monthStart = new Date(this.selectedYear, this.selectedMonth, 1)
      const monthEnd = new Date(this.selectedYear, this.selectedMonth + 1, 0, 23, 59, 59)
      return this.contracts.find(c =>
        c.clientId === clientId && c.start && c.start <= monthEnd && (!c.end || c.end >= monthStart)
      )
    },
    async getBillsForMonth () {
      this.bills = []
      const start = Timestamp.fromDate(new Date(this.selectedYear, this.selectedMonth, 1))
      const end = Timestamp.fromDate(new Date(this.selectedYear, this.selectedMonth + 1, 0, 23, 59, 59))
      const snap = await db.collectionGroup('bills')
        .where('deadline', '>=', start)
        .where('deadline', '<=', end)
        .orderBy('deadline', 'asc').get()
      snap.forEach(doc => {
        const obj = { id: doc.id, ...doc.data() }
        obj.deadline = doc.data().deadline.toDate()
        this.bills.push(obj)
      })
    },
    async getExchangeRate () {
      try {
        const now = new Date()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const date = String(now.getDate()).padStart(2, '0')
        const res = await axios.post(
          'https://southamerica-east1-inqi-5fe1e.cloudfunctions.net/vendors-getExchangeRate',
          { year: now.getFullYear(), month, date },
          { headers: { Authorization: 'Bearer ' + (process.env.VUE_APP_EXCHANGE_RATE_TOKEN || '78b1deb4-1b27-4943-9d83-818285cc59d6') } }
        )
        this.exchangeRate = res.data.venta || 3.7
      } catch { this.exchangeRate = 3.7 }
    },
    toUSD (amount, code) {
      if (!amount) return 0
      return code === 'USD' ? amount : (this.exchangeRate > 0 ? amount / this.exchangeRate : 0)
    },
    formatUSD (val) {
      return Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
    },
    pctClass (pct) {
      if (pct >= 80) return 'prop-row__pct--success'
      if (pct >= 50) return 'prop-row__pct--warning'
      return 'prop-row__pct--error'
    },
    barFillClass (pct) {
      if (pct >= 80) return ''
      if (pct >= 50) return 'prop-row__bar-fill--warning'
      return 'prop-row__bar-fill--error'
    },
    tenantRowClass (t) {
      if (t.overdue) return 'tenant-row--overdue'
      return ''
    },
    tenantStatusClass (t) {
      if (t.noBills) return 'tenant-row__status--expiring'
      if (t.paid) return 'tenant-row__status--ok'
      if (t.overdue) return 'tenant-row__status--overdue'
      return 'tenant-row__status--expiring'
    },
    tenantStatusLabel (t) {
      if (t.noBills) return 'Sin letra'
      if (t.paid) return 'Pagado'
      if (t.overdue) return 'Vencido'
      return 'Pendiente'
    },
    overduePeriod (date) {
      if (!date) return ''
      const d = date instanceof Date ? date : new Date(date)
      return new Intl.DateTimeFormat('es-PE', { month: 'short', year: 'numeric' }).format(d)
    },
    isExpanded (id) {
      return this.expandedIds.includes(id)
    },
    toggleEstate (id) {
      const idx = this.expandedIds.indexOf(id)
      if (idx === -1) this.expandedIds.push(id)
      else this.expandedIds.splice(idx, 1)
    },
    openPay (bill) {
      this.sidebarData = { ...bill }
      this.showPaySidebar = true
    },
    onPayUpdate (updatedBill) {
      this.showPaySidebar = false
      const bill = this.bills.find(b => b.id === updatedBill.id)
      if (bill) {
        bill.paid = true
        bill.total = updatedBill.total
      }
    },
    onBillAdded (newBill) {
      // Nueva letra por diferencia — no está en el mes actual, ignorar en la vista
      this.$toast.success('Nueva letra creada correctamente')
    }
  }
}
</script>

<style scoped>
.tenant-row__flag {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #9F1239;
  background: #FFE4E6;
  border-radius: 4px;
}
/* El patrón de card expandible (collection-list/card/sub, chevron, animación de apertura)
   vive en el global _list-row.scss — compartido con el módulo de Ventas.
   Aquí solo el ajuste propio de cobranza: el botón "Registrar pago" siempre visible. */
.collection-sub .tenant-row__actions {
  opacity: 1;
}

</style>
